import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Rocket,
  Eye,
  Plus,
  Paperclip,
  X,
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  Clock3,
  Hammer,
  FlaskConical,
  ChevronDown,
  NotebookPen,
  ChevronRight,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { api, ApiError, type Job, type Draft, type OpsModel, type Block, type Attachment, type RepoInfo, type Account } from '../api.js';
import { Screen, Section, Field, Seg, Btn, Sheet, Spinner, Badge, Toggle } from '../ui/kit.js';
import { reporter } from '../pwa/report.js';

// Compose is a working space now: the draft autosaves server-side on every
// pause in typing (reload, crash and device switch lose nothing), files
// attach and travel into the job's worktree with a manifest, blocks toggle
// per job, the kind decides the pipeline (Change: PR and CI; Workshop: files
// and a report back), and the exact assembled prompt is one look away — a
// live right-hand column on a desk, a sheet on the phone.

const AUTOSAVE_MS = 450;

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${Math.round(bytes / 102.4) / 10} KB`;
  return `${Math.round(bytes / 104857.6) / 10} MB`;
}

function attIcon(a: Attachment) {
  if (a.mime?.startsWith('image/')) return <ImageIcon size={14} />;
  if (a.mime?.startsWith('text/') || /\.(md|txt|csv|json)$/i.test(a.filename)) return <FileText size={14} />;
  return <FileIcon size={14} />;
}

export function Compose() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [models, setModels] = useState<OpsModel[]>([]);
  const [cliVersion, setCliVersion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(0);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [preview, setPreview] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [repos, setRepos] = useState<RepoInfo[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState<string>('');
  const [budgetUsd, setBudgetUsd] = useState('');
  const [budgetHours, setBudgetHours] = useState('');
  const [autoContinue, setAutoContinue] = useState(true);
  // Off by default: a job runs the model it was given, and parks when that
  // model is out everywhere rather than moving to the other one.
  const [autoSwitchModel, setAutoSwitchModel] = useState(false);
  const [branches, setBranches] = useState<string[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [schedule, setSchedule] = useState<'now' | 'later'>('now');
  const idempotency = useMemo(() => crypto.randomUUID(), []);
  const saveTimer = useRef<number | null>(null);
  const pendingPatch = useRef<Record<string, unknown>>({});
  const draftId = draft?.id ?? null;

  const loadBlocks = () => api.get<{ templates: Block[] }>('/api/templates').then((r) => setBlocks(r.templates));

  useEffect(() => {
    void loadBlocks();
    api
      .get<{ models: OpsModel[]; cliVersion: string; jobDefaults: { model: string; effort: string | null; mode: 'hold' | 'ship' } }>(
        '/api/settings',
      )
      .then((s) => {
        setModels(s.models);
        setCliVersion(s.cliVersion);
        setDraft((d) =>
          d && !d.model ? { ...d, model: s.jobDefaults.model, effort: s.jobDefaults.effort, mode: s.jobDefaults.mode } : d,
        );
      })
      .catch(reporter('the composer'));
    api
      .get<{ draft: Draft }>('/api/drafts/active')
      .then((r) => {
        setDraft(r.draft);
        setSchedule(r.draft.scheduledFor ? 'later' : 'now');
      })
      .catch(reporter('the composer'));
    api
      .get<{ jobs: Job[] }>('/api/jobs?scope=active')
      .then((r) => setQueued(r.jobs.filter((j) => j.phase === 'queued').length))
      .catch(reporter('the composer'));
    api
      .get<{ repos: RepoInfo[] }>('/api/repos')
      .then((r) => setRepos(r.repos))
      .catch(reporter('the composer'));
    api
      .get<{ accounts: Account[] }>('/api/accounts')
      .then((r) => {
        setAccounts(r.accounts);
        setAccountId((cur) => cur || r.accounts[0]?.id || '');
      })
      .catch(reporter('the composer'));
  }, []);

  // The target repo's real branches feed the base-branch dropdown; a repo
  // switch re-reads them and lands back on that repo's default.
  const repoId = draft?.repo ?? 'meridian';
  useEffect(() => {
    let alive = true;
    setBranches([]);
    void api
      .get<{ branches: string[]; default: string }>(`/api/repos/${repoId}/branches`)
      .then((r) => {
        if (alive) setBranches(r.branches);
      })
      .catch(reporter('the composer'));
    return () => {
      alive = false;
    };
  }, [repoId]);

  // One debounced PATCH carries every field that changed since the last save.
  const patch = useCallback(
    (fields: Record<string, unknown>) => {
      setDraft((d) => (d ? ({ ...d, ...fields } as Draft) : d));
      Object.assign(pendingPatch.current, fields);
      if (!draftId) return;
      setSaving('saving');
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        const body = pendingPatch.current;
        pendingPatch.current = {};
        void api
          .patch<{ draft: Draft }>(`/api/drafts/${draftId}`, body)
          .then(() => setSaving('saved'))
          .catch(() => setSaving('idle'));
      }, AUTOSAVE_MS);
    },
    [draftId],
  );

  // The live preview follows the draft, a beat behind the autosave.
  useEffect(() => {
    if (!draftId) return;
    const t = window.setTimeout(() => {
      void api
        .get<{ prompt: string }>(`/api/drafts/${draftId}/preview`)
        .then((r) => setPreview(r.prompt))
        .catch(reporter('the composer'));
    }, AUTOSAVE_MS + 350);
    return () => window.clearTimeout(t);
  }, [draftId, draft?.prompt, draft?.templateIds, draft?.kind, draft?.mode, draft?.model, draft?.effort, draft?.baseBranch, draft?.repo, draft?.referenceRepo, draft?.attachments.length, saving]);

  const [uploading, setUploading] = useState<string[]>([]);
  const attach = useCallback(
    (files: File[]) => {
      if (!draftId || !files.length) return;
      const names = files.map((f) => f.name || 'file');
      setUploading((u) => [...u, ...names]);
      void api
        .upload<{ draft: Draft }>(`/api/drafts/${draftId}/attachments`, files)
        .then((r) => setDraft(r.draft))
        .catch((err) => setError(err instanceof ApiError ? err.message : 'Upload failed.'))
        .finally(() => setUploading((u) => u.filter((n) => !names.includes(n))));
    },
    [draftId],
  );

  // Paste-as-file: long text lands as an attachment instead of drowning the
  // prompt. Short pastes behave like normal typing.
  const onPaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const fileItems = items.filter((i) => i.kind === 'file');
    if (fileItems.length) {
      const files = fileItems.map((i) => i.getAsFile()).filter(Boolean) as File[];
      if (files.length) {
        e.preventDefault();
        attach(files);
        return;
      }
    }
    const text = e.clipboardData.getData('text/plain');
    if (text.length > 2000) {
      e.preventDefault();
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      attach([new File([text], `pasted-${stamp}.txt`, { type: 'text/plain' })]);
    }
  };

  const removeAttachment = (aid: string) => {
    if (!draftId) return;
    void api.del(`/api/drafts/${draftId}/attachments/${aid}`).catch(reporter('removing the file')).then(() => {
      setDraft((d) => (d ? { ...d, attachments: d.attachments.filter((a) => a.id !== aid) } : d));
    });
  };

  const switchDraft = async (id: string) => {
    const r = await api.patch<{ draft: Draft }>(`/api/drafts/${id}`, { activate: true });
    setDraft(r.draft);
    setSchedule(r.draft.scheduledFor ? 'later' : 'now');
    setShowDrafts(false);
  };

  const newDraft = async () => {
    const r = await api.post<{ draft: Draft }>('/api/drafts', {});
    setDraft(r.draft);
    setSchedule('now');
    setShowDrafts(false);
  };

  const openDrafts = async () => {
    const r = await api.get<{ drafts: Draft[] }>('/api/drafts');
    setDrafts(r.drafts);
    setShowDrafts(true);
  };

  const kind = draft?.kind ?? 'change';
  const defaultBranch = repos.find((r) => r.id === repoId)?.defaultBranch ?? 'main';
  const mode = (draft?.mode as 'hold' | 'ship' | null) ?? 'hold';
  const model = draft?.model ?? 'fable';
  const currentModel = models.find((m) => m.id === model);
  const effort = draft?.effort ?? null;

  const send = async () => {
    if (!draftId) return;
    setBusy(true);
    setError(null);
    try {
      const { job } = await api.post<{ job: Job }>('/api/jobs', {
        draftId,
        idempotencyKey: idempotency,
        scheduledFor: schedule === 'later' ? draft?.scheduledFor : null,
        accountId: accountId || undefined,
        budgetUsd: budgetUsd.trim() ? Number(budgetUsd) : null,
        budgetHours: budgetHours.trim() ? Number(budgetHours) : null,
        autoContinueOtherAccount: autoContinue,
        autoSwitchModel,
      });
      navigate(`/jobs/${job.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start the job.');
      setBusy(false);
    }
  };

  const previewPanel = (
    <div className="ops-preview">
      <div className="ops-preview__head">
        <b>The exact prompt</b>
        <span>{preview ? `${preview.length.toLocaleString()} characters` : ''}</span>
      </div>
      <pre className="ops-preview__body">{preview || 'Assembling…'}</pre>
    </div>
  );

  return (
    <Screen
      eyebrow="Meridian Ops"
      title="Compose"
      sub="Say the change. The box builds it, proves it, and shows you before it ships."
      actions={
        <Btn kind="ghost" onClick={() => void openDrafts()}>
          <NotebookPen size={16} />
          {draft ? draft.name : 'Drafts'}
          <ChevronDown size={13} />
        </Btn>
      }
    >
      <div className="ops-cols">
        <div>
          <div
            className={`ops-dropzone${dragOver ? ' ops-dropzone--over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              attach(Array.from(e.dataTransfer.files));
            }}
          >
            <textarea
              className="ops-textarea ops-textarea--prompt"
              placeholder="What should change?"
              value={draft?.prompt ?? ''}
              onChange={(e) => patch({ prompt: e.target.value })}
              onPaste={onPaste}
            />
            {dragOver ? <div className="ops-dropzone__note">Drop to attach</div> : null}
          </div>

          <div className="ops-attachrow">
            <label className="ops-attach-btn">
              <Paperclip size={15} />
              Attach files
              <input
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  attach(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
            </label>
            <span className="ops-savenote" aria-live="polite">
              {saving === 'saving' ? 'Saving…' : saving === 'saved' ? 'Saved' : ''}
            </span>
          </div>

          {draft?.attachments.length ? (
            <div className="ops-attachments">
              {draft.attachments.map((a) => (
                <span
                  key={a.id}
                  className={`ops-attachment${a.state === 'failed' ? ' ops-attachment--failed' : ''}`}
                  data-testid={`attachment-${a.state ?? 'stored'}`}
                >
                  {a.mime?.startsWith('image/') ? (
                    <img src={`/api/drafts/${draft.id}/attachments/${a.id}`} alt="" />
                  ) : (
                    attIcon(a)
                  )}
                  <span className="ops-attachment__name">{a.filename}</span>
                  <span className="ops-attachment__size">{fmtSize(a.size)}</span>
                  {a.state === 'failed' ? (
                    <AlertTriangle size={13} color="var(--ops-bad)" aria-label={a.error ?? 'failed'} />
                  ) : (
                    <Check size={13} color="var(--ops-ok)" aria-label="verified" />
                  )}
                  <button type="button" aria-label={`Remove ${a.filename}`} onClick={() => removeAttachment(a.id)}>
                    <X size={13} />
                  </button>
                </span>
              ))}
              {uploading.map((name, i) => (
                <span key={`up-${i}`} className="ops-attachment ops-attachment--uploading">
                  <Spinner />
                  <span className="ops-attachment__name">{name}</span>
                  <span className="ops-attachment__size">uploading</span>
                </span>
              ))}
            </div>
          ) : null}

          <Section
            title="Blocks"
            aside={
              <button type="button" className="ops-btn ops-btn--ghost" onClick={() => navigate('/blocks')}>
                Manage
              </button>
            }
          >
            {blocks.length === 0 ? (
              <p className="ops-hint" style={{ margin: '2px 2px 0' }}>
                Your standing instructions live here. Write blocks once under More, toggle them per job. Blocks
                marked "always" ride every job.
              </p>
            ) : (
              <div className="ops-chips">
                {blocks.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="ops-chip"
                    aria-pressed={t.always === 1 || (draft?.templateIds ?? []).includes(t.id)}
                    onClick={() => {
                      if (t.always === 1) return navigate('/blocks');
                      const cur = draft?.templateIds ?? [];
                      patch({
                        templateIds: cur.includes(t.id) ? cur.filter((x) => x !== t.id) : [...cur, t.id],
                      });
                    }}
                  >
                    {t.title}
                    {t.always === 1 ? <Badge tone="accent">always</Badge> : null}
                    {t.pin ? <Badge tone="wait">{t.pin === 'begin' ? 'first' : 'last'}</Badge> : null}
                  </button>
                ))}
                <button type="button" className="ops-chip" aria-label="New block" onClick={() => navigate('/blocks')}>
                  <Plus size={14} />
                </button>
              </div>
            )}
          </Section>

          <Section title="Repository">
            <Seg
              big
              value={repoId}
              onChange={(rp) => {
                const def = repos.find((r) => r.id === rp);
                patch({ repo: rp, baseBranch: def?.defaultBranch ?? 'main', referenceRepo: null });
              }}
              options={(repos.length
                ? repos
                : [{ id: 'meridian', label: 'Meridian', defaultBranch: 'main', capabilities: { ci: true, staging: true } } as RepoInfo]
              ).map((rp) => ({
                value: rp.id,
                label: (
                  <>
                    <span>{rp.label}</span>
                    <small>
                      {rp.capabilities.ci ? 'CI, staging, ceremony' : rp.capabilities.pagesDeploy ? `no CI, deploys to ${rp.capabilities.pagesDeploy.liveUrl.replace('https://', '')}` : 'no CI'}
                    </small>
                  </>
                ),
              }))}
            />
            {repos.length > 1 ? (
              <div className="ops-row" style={{ marginTop: 6 }}>
                <div className="ops-row__main">
                  <div className="ops-row__title">
                    Mount {repos.find((r) => r.id !== repoId)?.label ?? 'the other repo'} read-only as reference
                  </div>
                  <div className="ops-row__sub">
                    {draft?.referenceRepo
                      ? `The session finds it at .ops-reference/${draft.referenceRepo} inside the worktree, reads everything, commits nothing.`
                      : 'For porting: the session reads the other repo beside its worktree.'}
                  </div>
                </div>
                <Toggle
                  checked={Boolean(draft?.referenceRepo)}
                  onChange={(v) => {
                    const other = repos.find((r) => r.id !== repoId);
                    patch({ referenceRepo: v && other ? other.id : null });
                  }}
                  label="mount reference"
                />
              </div>
            ) : null}
          </Section>

          <Section title="Kind of job">
            <Seg
              big
              value={kind}
              onChange={(k) => patch({ kind: k })}
              options={[
                {
                  value: 'change',
                  label: (
                    <>
                      <span>
                        <Hammer size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
                        Change
                      </span>
                      <small>branch, PR, CI, ship</small>
                    </>
                  ),
                },
                {
                  value: 'workshop',
                  label: (
                    <>
                      <span>
                        <FlaskConical size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
                        Workshop
                      </span>
                      <small>no PR, files and a report back</small>
                    </>
                  ),
                },
              ]}
            />
          </Section>

          {kind === 'change' ? (
            <Section title="When CI is green">
              <Seg
                big
                value={mode}
                onChange={(m) => patch({ mode: m })}
                options={[
                  {
                    value: 'hold',
                    label: (
                      <>
                        <span>
                          <Eye size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
                          Hold for my review
                        </span>
                        <small>screenshots first, you ship it</small>
                      </>
                    ),
                  },
                  {
                    value: 'ship',
                    ship: true,
                    label: (
                      <>
                        <span>
                          <Rocket size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
                          Ship it
                        </span>
                        <small>merges itself on green</small>
                      </>
                    ),
                  },
                ]}
              />
            </Section>
          ) : null}

          <Section title="Model">
            <Seg
              value={model}
              onChange={(m) => {
                const eff = models.find((x) => x.id === m)?.efforts ?? [];
                patch({ model: m, effort: eff.length ? (effort && eff.includes(effort) ? effort : 'high') : null });
              }}
              big
              options={models.map((m) => ({
                value: m.id,
                label: (
                  <>
                    <span>{m.label}</span>
                    <small className="ops-modelid" data-testid={`compose-model-id-${m.id}`}>
                      {m.cli}
                    </small>
                  </>
                ),
              }))}
            />
            {currentModel ? (
              <p className="ops-hint" data-testid="compose-account">
                Runs <span className="ops-sha">{currentModel.cli}</span>
                {cliVersion ? ` on Claude Code ${cliVersion.replace(/\s*\(.*\)$/, '')}` : ''}
                {accounts.find((a) => a.id === accountId) ? <> under <b>{accounts.find((a) => a.id === accountId)!.label}</b></> : null}. The
                job page shows the model the session itself reports.
              </p>
            ) : null}
            {currentModel && cliVersion && cliTooOld(cliVersion, currentModel.minCli) ? (
              <p className="ops-hint ops-hint--bad" data-testid="compose-cli-too-old">
                Claude Code on the box is older than {currentModel.minCli}, the first release that knows {currentModel.label}. Update it
                on the box before sending this job.
              </p>
            ) : null}
            {currentModel?.efforts.length ? (
              <div style={{ marginTop: 8 }}>
                <Seg
                  value={effort ?? 'high'}
                  onChange={(e) => patch({ effort: e })}
                  options={currentModel.efforts.map((e) => ({ value: e, label: e }))}
                />
              </div>
            ) : currentModel ? (
              <p className="ops-hint">This model has no effort control in the CLI.</p>
            ) : null}
          </Section>

          <div className="ops-advanced">
            <button
              type="button"
              className="ops-advanced__toggle"
              aria-expanded={advancedOpen}
              onClick={() => setAdvancedOpen((v) => !v)}
            >
              <ChevronRight size={14} className={`ops-advanced__chev${advancedOpen ? ' ops-advanced__chev--open' : ''}`} />
              Advanced
              {!advancedOpen ? (
                <span className="ops-advanced__summary">
                  {(draft?.baseBranch ?? 'main') !== defaultBranch ? ` · base ${draft?.baseBranch}` : ''}
                  {schedule === 'later' && draft?.scheduledFor ? ' · scheduled' : ''}
                  {autoSwitchModel ? ' · may move model' : ''}
                </span>
              ) : null}
            </button>
            {advancedOpen ? (
              <div className="ops-advanced__body">
                {kind === 'change' ? (
                  <Field label="Base branch" hint="The branch this job starts from. Almost always the default.">
                    <select
                      className="ops-input ops-select"
                      value={draft?.baseBranch ?? defaultBranch}
                      onChange={(e) => patch({ baseBranch: e.target.value })}
                    >
                      {(branches.length ? branches : [draft?.baseBranch ?? defaultBranch]).map((b) => (
                        <option key={b} value={b}>
                          {b}
                          {b === defaultBranch ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}
                {accounts.length > 1 ? (
                  <Field label="Account" hint="The token this job's session runs under. A limit continues on the other one unless you switch that off.">
                    <Seg value={accountId} onChange={(v) => setAccountId(v)} options={accounts.map((a) => ({ value: a.id, label: a.label }))} />
                  </Field>
                ) : null}
                <Field label="Budget per attempt" hint="Optional. Reaching it ends the attempt cleanly; Continue starts a fresh one.">
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="ops-input" inputMode="decimal" placeholder="dollars" value={budgetUsd} onChange={(e) => setBudgetUsd(e.target.value)} data-testid="compose-budget-usd" />
                    <input className="ops-input" inputMode="decimal" placeholder="hours" value={budgetHours} onChange={(e) => setBudgetHours(e.target.value)} data-testid="compose-budget-hours" />
                  </div>
                </Field>
                {accounts.length > 1 ? (
                  <div className="ops-row ops-row--wrap" style={{ marginBottom: 8 }}>
                    <div className="ops-row__main">
                      <div className="ops-row__title">Continue on the other account when a limit is hit</div>
                      <div className="ops-row__sub">On by default. The job page says when it happened.</div>
                    </div>
                    <Toggle checked={autoContinue} onChange={setAutoContinue} label="continue on the other account" />
                  </div>
                ) : null}
                {models.length > 1 && currentModel ? (
                  <div className="ops-row ops-row--wrap" style={{ marginBottom: 8 }}>
                    <div className="ops-row__main">
                      <div className="ops-row__title">Move to the other model when this one runs out</div>
                      <div className="ops-row__sub">
                        Off by default. The job keeps {currentModel.label} and parks until it resets. On, it continues on{' '}
                        {models.find((m) => m.id !== model)?.label ?? 'the other model'} and the job page says so.
                      </div>
                    </div>
                    <Toggle checked={autoSwitchModel} onChange={setAutoSwitchModel} label="move to the other model when this one runs out" testId="compose-auto-switch-model" />
                  </div>
                ) : null}
                <Field label="Run">
                  <Seg
                    value={schedule}
                    onChange={(v) => {
                      setSchedule(v);
                      if (v === 'now') patch({ scheduledFor: null });
                    }}
                    options={[
                      { value: 'now', label: 'Now' },
                      {
                        value: 'later',
                        label: (
                          <span>
                            <Clock3 size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
                            At a time
                          </span>
                        ),
                      },
                    ]}
                  />
                  {schedule === 'later' ? (
                    <div style={{ marginTop: 8 }}>
                      <input
                        type="datetime-local"
                        className="ops-input"
                        value={draft?.scheduledFor ? draft.scheduledFor.slice(0, 16) : ''}
                        min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                        onChange={(e) =>
                          patch({ scheduledFor: e.target.value ? new Date(e.target.value).toISOString() : null })
                        }
                      />
                      <p className="ops-hint">The box starts it at that time and pings you as usual.</p>
                    </div>
                  ) : null}
                </Field>
              </div>
            ) : null}
          </div>

          <div style={{ marginTop: 22 }}>
            <Btn
              kind="primary"
              big
              disabled={busy || (draft?.prompt.trim().length ?? 0) < 8}
              onClick={() => void send()}
            >
              {busy ? (
                <Spinner />
              ) : schedule === 'later' && draft?.scheduledFor ? (
                'Schedule it'
              ) : kind === 'workshop' ? (
                'Run the workshop'
              ) : mode === 'ship' ? (
                'Build and ship'
              ) : (
                'Build it'
              )}
            </Btn>
            {queued > 0 ? (
              <div className="ops-queuepos">
                <Badge tone="wait">{queued} queued</Badge> This job waits its turn. One at a time on the box.
              </div>
            ) : null}
            {error ? <p className="ops-error">{error}</p> : null}
            <p className="ops-hint" style={{ marginTop: 12 }}>
              <button type="button" className="ops-linklike" onClick={() => setShowPreview(true)}>
                See the exact prompt this sends
              </button>
            </p>
          </div>
        </div>

        <div className="ops-cols__side">{previewPanel}</div>
      </div>

      {showPreview ? (
        <Sheet title="The exact prompt" onClose={() => setShowPreview(false)}>
          <pre className="ops-preview__body ops-preview__body--sheet">{preview || 'Assembling…'}</pre>
        </Sheet>
      ) : null}

      {showDrafts ? (
        <Sheet title="Drafts" onClose={() => setShowDrafts(false)}>
          <div className="ops-rows">
            {drafts.map((d) => (
              <div key={d.id} className="ops-row" style={{ paddingRight: 8 }}>
                <button
                  type="button"
                  className="ops-row__main"
                  style={{ textAlign: 'left', minHeight: 36 }}
                  onClick={() => void switchDraft(d.id)}
                >
                  <div className="ops-row__title">
                    {d.name}
                    {d.id === draftId ? ' · open' : ''}
                  </div>
                  <div className="ops-row__sub">
                    {(d.prompt || 'Empty').slice(0, 80)}
                    {d.attachments.length ? ` · ${d.attachments.length} file${d.attachments.length > 1 ? 's' : ''}` : ''}
                  </div>
                </button>
                {d.id !== draftId ? (
                  <button
                    type="button"
                    className="ops-btn ops-btn--ghost"
                    aria-label={`Delete ${d.name}`}
                    onClick={() => {
                      void api
                        .del(`/api/drafts/${d.id}`)
                        .then(() => setDrafts((x) => x.filter((y) => y.id !== d.id)))
                        .catch(reporter('deleting the draft'));
                    }}
                  >
                    <X size={15} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
            <Btn kind="primary" onClick={() => void newDraft()}>
              <Plus size={15} /> New draft
            </Btn>
            {draft ? (
              <input
                className="ops-input"
                style={{ flex: 1 }}
                value={draft.name}
                aria-label="Draft name"
                onChange={(e) => patch({ name: e.target.value })}
              />
            ) : null}
          </div>
        </Sheet>
      ) : null}
    </Screen>
  );
}

/** The box's Claude Code is older than the first release that knows a model. */
function cliTooOld(version: string, min: string): boolean {
  // "unknown" is not old: it is unread, and the Settings page says so.
  if (!/\d+\.\d+/.test(version)) return false;
  const parse = (v: string) => (v.match(/\d+(\.\d+)*/)?.[0] ?? '0').split('.').map(Number);
  const a = parse(version);
  const b = parse(min);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d < 0;
  }
  return false;
}
